import React, {useState, useEffect, useRef, useCallback} from 'react';
import {View, StyleSheet, KeyboardAvoidingView, Platform, TouchableOpacity} from 'react-native';
import {Text, TextInput, Button, Surface} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {authApi} from '../../services/api/authApi';
import {useAuthStore} from '../../store/useAuthStore';
import CountryPickerModal from '../../components/CountryPickerModal';
import {colors, spacing} from '../../theme';

const RESEND_COOLDOWN = 30;

const getFlag = code =>
  String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt()),
  );

const LoginScreen = ({navigation}) => {
  const {t} = useTranslation();
  const loginWithOtp = useAuthStore(state => state.loginWithOtp);
  const [countryCode, setCountryCode] = useState('+91');
  const [countryIso, setCountryIso] = useState('IN');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickerVisible, setPickerVisible] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startResendTimer = useCallback(() => {
    setResendTimer(RESEND_COOLDOWN);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendOtp = async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authApi.requestOtp({countryCode, phone: phone.trim(), purpose: 'LOGIN'});
      setMaskedEmail(res.data?.data || '');
      setOtpSent(true);
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setOtp('');
    try {
      const res = await authApi.requestOtp({countryCode, phone: phone.trim(), purpose: 'LOGIN'});
      setMaskedEmail(res.data?.data || '');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await loginWithOtp(countryCode, phone.trim(), otp.trim());
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = country => {
    setCountryCode(country.dialCode);
    setCountryIso(country.code);
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.content}>
        <Text variant="headlineLarge" style={styles.title}>
          FabTailor
        </Text>
        <Text variant="bodyLarge" style={styles.subtitle}>
          {t('auth.login')}
        </Text>

        <Surface style={styles.form} elevation={1}>
          <View style={styles.phoneRow}>
            <TouchableOpacity
              style={styles.countryButton}
              onPress={() => setPickerVisible(true)}
              disabled={otpSent}>
              <Text style={styles.flag}>{getFlag(countryIso)}</Text>
              <Text style={styles.dialCode}>{countryCode}</Text>
              <Icon name="chevron-down" size={18} color={colors.textSecondary} />
            </TouchableOpacity>

            <TextInput
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
              placeholder={t('auth.phone')}
              mode="outlined"
              style={styles.phoneInput}
              disabled={otpSent}
            />
          </View>

          {otpSent && (
            <>
              <Text style={styles.otpMessage}>
                {maskedEmail
                  ? `Enter the OTP sent to your registered email - ${maskedEmail}`
                  : t('auth.enterOtp')}
              </Text>
              <TextInput
                label={t('auth.otp')}
                value={otp}
                onChangeText={setOtp}
                keyboardType="number-pad"
                maxLength={6}
                mode="outlined"
                style={styles.input}
                autoFocus
              />
              <Button
                mode="text"
                onPress={handleResendOtp}
                disabled={resendTimer > 0 || loading}
                compact
                style={styles.resendButton}
                labelStyle={styles.resendLabel}>
                {resendTimer > 0
                  ? `Resend OTP in ${resendTimer}s`
                  : 'Resend OTP'}
              </Button>
            </>
          )}

          {error ? <Text style={styles.errorText}>{error}</Text> : null}

          <Button
            mode="contained"
            onPress={otpSent ? handleVerifyOtp : handleSendOtp}
            loading={loading}
            disabled={loading}
            style={styles.button}>
            {otpSent ? t('auth.verifyOtp') : t('auth.sendOtp')}
          </Button>

          {otpSent && (
            <Button
              mode="text"
              onPress={() => {
                setOtpSent(false);
                setOtp('');
                setError('');
              }}
              style={styles.linkButton}>
              Change Phone Number
            </Button>
          )}
        </Surface>

        <Button
          mode="text"
          onPress={() => navigation.navigate('RegisterStep1')}
          style={styles.linkButton}>
          {t('auth.dontHaveAccount')} {t('auth.register')}
        </Button>
      </View>

      <CountryPickerModal
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        onSelect={handleCountrySelect}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {flex: 1, justifyContent: 'center', padding: spacing.lg},
  title: {textAlign: 'center', color: colors.primary, fontWeight: 'bold'},
  subtitle: {textAlign: 'center', marginBottom: spacing.lg, color: colors.textSecondary},
  form: {padding: spacing.lg, borderRadius: 12, backgroundColor: colors.surface},
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  countryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: 12,
    height: 56,
    marginRight: 8,
  },
  flag: {fontSize: 20, marginRight: 4},
  dialCode: {fontSize: 16, color: colors.text, marginRight: 2},
  phoneInput: {flex: 1, backgroundColor: colors.surface},
  input: {marginBottom: spacing.sm},
  otpMessage: {color: colors.textSecondary, marginBottom: spacing.sm},
  button: {marginTop: spacing.md},
  resendButton: {alignSelf: 'flex-end', marginTop: -spacing.xs},
  resendLabel: {fontSize: 13},
  linkButton: {marginTop: spacing.sm},
  errorText: {color: colors.error, fontSize: 12, marginBottom: spacing.xs},
});

export default LoginScreen;
